import { useEffect, useState } from 'react'
import { captureFix, deviceId, fixQuality, qualityMessage, type Fix } from '../lib/geo'
import { localDuplicateCandidates, type LocalCandidate } from '../lib/duplicates'
import { db, localRefOptions, localHouseholds, type LocalHousehold } from '../lib/db'
import { queue } from '../lib/sync'
import { queueAssessment, queueCreateHousehold } from '../lib/outbox'
import { t } from '../lib/i18n'

/**
 * REGISTERING A HOUSEHOLD, WITH OR WITHOUT SIGNAL
 * -----------------------------------------------
 * This screen no longer knows whether the phone is online, and that is the
 * point. It writes the household to the phone, puts two operations in the
 * outbox, and returns. Uploading is somebody else's job.
 *
 * The duplicate check runs against the households cached on the phone, using
 * the same thresholds and the same similarity measure as the database. An
 * officer gets the same answer standing in a village with no signal as they
 * would sitting in the office, which is the only version of this feature worth
 * having — a safety check that quietly switches itself off in the field is
 * worse than none, because people trust it.
 */

type RefOption = { list_code: string; code: string; label_en: string; label_km: string | null }

const COUNTS = [
  ['adults_male_18_59', 'Adult men 18–59', 'បុរស ១៨-៥៩'],
  ['adults_female_18_59', 'Adult women 18–59', 'ស្ត្រី ១៨-៥៩'],
  ['children_under_18', 'Children under 18', 'កុមារក្រោម ១៨'],
  ['elderly_60_plus', 'People 60 and over', 'អាយុ ៦០ ឡើង'],
  ['members_with_disability', 'People with a disability', 'ជនពិការ'],
] as const

type CountKey = (typeof COUNTS)[number][0]

export function AddHouseholdScreen({
  villageId,
  villageName,
  onDone,
  onCancel,
}: {
  villageId: string
  villageName: string
  onDone: (label: string) => void
  onCancel: () => void
}) {
  const [options, setOptions] = useState<RefOption[]>([])
  const [nearby, setNearby] = useState<LocalHousehold[]>([])
  const [headName, setHeadName] = useState('')
  const [headSex, setHeadSex] = useState('')
  const [counts, setCounts] = useState<Record<CountKey, number>>({
    adults_male_18_59: 0,
    adults_female_18_59: 0,
    children_under_18: 0,
    elderly_60_plus: 0,
    members_with_disability: 0,
  })
  const [hasLatrine, setHasLatrine] = useState<boolean | null>(null)
  const [latrineType, setLatrineType] = useState('')
  const [waterSource, setWaterSource] = useState('')

  const [fix, setFix] = useState<Fix | null>(null)
  const [capturing, setCapturing] = useState(false)
  const [progress, setProgress] = useState('')

  const [candidates, setCandidates] = useState<LocalCandidate[] | null>(null)
  const [confirmedNotDuplicate, setConfirmedNotDuplicate] = useState(false)

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const size =
    counts.adults_male_18_59 +
    counts.adults_female_18_59 +
    counts.children_under_18 +
    counts.elderly_60_plus

  // Read the code lists and the neighbours FROM THE PHONE. If a refresh has
  // never run, both are empty and the screen still works — the officer just
  // sees fewer options, which is better than a screen that will not open.
  useEffect(() => {
    localRefOptions(['sex', 'latrine_type', 'water_source']).then((rows) =>
      setOptions(rows as RefOption[]),
    )
    localHouseholds(villageId).then(setNearby)
  }, [villageId])

  const opts = (list: string) => options.filter((o) => o.list_code === list)

  function bump(key: CountKey, by: number) {
    setCounts((c) => ({ ...c, [key]: Math.max(0, Math.min(60, c[key] + by)) }))
  }

  async function grabLocation() {
    setError(null)
    setCapturing(true)
    setProgress('Starting…')
    setCandidates(null)
    setConfirmedNotDuplicate(false)

    const { promise } = captureFix({
      onProgress: (p) =>
        setProgress(
          p.best == null
            ? `${t('add.searching')} ${p.seconds}s`
            : `Best so far ±${p.best} m · ${p.samples} readings · ${p.seconds}s`,
        ),
    })

    try {
      setFix(await promise)
      setProgress('')
    } catch (e) {
      setError((e as Error).message)
      setProgress('')
    } finally {
      setCapturing(false)
    }
  }

  async function save() {
    setError(null)
    if (!fix) {
      setError('Capture the location first.')
      return
    }

    setSaving(true)
    try {
      if (!confirmedNotDuplicate) {
        const found = localDuplicateCandidates(
          nearby.map((h) => ({
            household_uid: h.household_uid,
            household_code: h.household_code,
            head_name: h.head_name,
            lat: h.lat,
            lon: h.lon,
            record_status: h.record_status,
          })),
          fix.lat,
          fix.lon,
          headName || null,
        )
        if (found.length > 0) {
          setCandidates(found)
          setSaving(false)
          return // nothing written; the officer decides
        }
      }

      // THE IDENTIFIER IS CHOSEN HERE, ON THE PHONE (migration 011). That is
      // what lets the assessment below name this house before any server has
      // heard of it.
      const householdUid = crypto.randomUUID()
      const now = new Date().toISOString()

      const values: Record<string, unknown> = { ...counts }
      if (hasLatrine !== null) values.has_latrine = hasLatrine
      if (latrineType) values.latrine_type = latrineType
      if (waterSource) values.water_source = waterSource

      // 1. The phone's own copy, so the officer sees it in the list at once.
      await db.households.put({
        household_uid: householdUid,
        village_id: villageId,
        household_code: null, // KH-…-00007 is the server's to assign
        head_name: headName || null,
        head_sex: headSex || null,
        household_size: size,
        adults_male_18_59: counts.adults_male_18_59,
        adults_female_18_59: counts.adults_female_18_59,
        children_under_18: counts.children_under_18,
        elderly_60_plus: counts.elderly_60_plus,
        members_with_disability: counts.members_with_disability,
        record_status: 'active',
        has_latrine: hasLatrine,
        latrine_type: latrineType || null,
        water_source: waterSource || null,
        gps_accuracy_m: fix.accuracy_m,
        gps_captured_at: fix.captured_at,
        last_assessed_at: now,
        updated_at: now,
        lat: fix.lat,
        lon: fix.lon,
        pending: true,
      })

      // 2. Two operations, two op_ids. If the phone dies between them the house
      //    exists with a "never assessed" flag — visible and fixable, not lost.
      await queue(
        queueCreateHousehold({
          household_uid: householdUid,
          village_id: villageId,
          head_name: headName || null,
          head_sex: headSex || null,
          counts,
          lat: fix.lat,
          lon: fix.lon,
          gps_accuracy_m: fix.accuracy_m,
          gps_provider: fix.provider,
          gps_captured_at: fix.captured_at,
          device: deviceId(),
        }),
      )

      await queue(
        queueAssessment({
          household_uid: householdUid,
          values,
          purpose: 'registration',
          // The moment the officer stood there. Not the moment it uploads.
          observed_at: fix.captured_at,
          lat: fix.lat,
          lon: fix.lon,
          gps_accuracy_m: fix.accuracy_m,
          device: deviceId(),
        }),
      )

      onDone(headName || 'Household')
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const blocked = candidates?.some((c) => c.severity === 'block') ?? false
  const selectStyle = {
    width: '100%',
    minHeight: 48,
    fontSize: 16,
    borderRadius: 10,
    border: '2px solid var(--line)',
    padding: '10px 12px',
  }

  return (
    <div className="screen">
      <div className="appbar">
        <h1>{t('add.title')} · {villageName}</h1>
        <button className="btn-quiet" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>

      <div className="content">
        <div className="card" style={{ maxWidth: 'none' }}>
          <h3 style={{ marginTop: 0 }}>{t('add.step1')}</h3>

          {!fix && (
            <p className="muted" style={{ marginTop: 0 }}>
              {t('add.standInFront')}
            </p>
          )}

          <button className="btn-primary" onClick={grabLocation} disabled={capturing || saving}>
            {capturing ? t('add.capturing') : fix ? t('add.captureAgain') : t('add.capture')}
          </button>

          {progress && <p className="muted">{progress}</p>}

          {fix && (
            <div
              className={fixQuality(fix.accuracy_m) === 'poor' ? 'alert alert-warn' : 'alert'}
              style={fixQuality(fix.accuracy_m) === 'poor' ? undefined : { borderColor: 'var(--line)' }}
            >
              {qualityMessage(fix.accuracy_m)}
              <div className="muted" style={{ marginTop: 4 }}>
                {fix.lat.toFixed(5)}, {fix.lon.toFixed(5)} · {fix.samples} {t('add.readings')} · {fix.provider}
              </div>
            </div>
          )}
        </div>

        <div className="card" style={{ maxWidth: 'none', marginTop: 14 }}>
          <h3 style={{ marginTop: 0 }}>{t('add.step2')}</h3>

          <label htmlFor="head">{t('add.headName')}</label>
          <input id="head" type="text" value={headName} onChange={(e) => setHeadName(e.target.value)} />

          <label htmlFor="sex">{t('add.headSex')}</label>
          <select id="sex" value={headSex} onChange={(e) => setHeadSex(e.target.value)} style={selectStyle}>
            <option value="">{t('common.notRecorded')}</option>
            {opts('sex').map((o) => (
              <option key={o.code} value={o.code}>
                {o.label_en}
                {o.label_km ? ` · ${o.label_km}` : ''}
              </option>
            ))}
          </select>

          <div style={{ marginTop: 16 }}>
            {COUNTS.map(([key, en, km]) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{en}</div>
                  <div className="muted km">{km}</div>
                </div>
                <button style={{ width: 48 }} onClick={() => bump(key, -1)} aria-label={`fewer ${en}`}>
                  −
                </button>
                <span style={{ minWidth: 32, textAlign: 'center', fontSize: 20, fontWeight: 700 }}>
                  {counts[key]}
                </span>
                <button style={{ width: 48 }} onClick={() => bump(key, +1)} aria-label={`more ${en}`}>
                  +
                </button>
              </div>
            ))}
          </div>

          <p className="muted" style={{ marginBottom: 0 }}>
            {t('add.householdSize')}: <strong>{size}</strong>. {t('add.disabilityNote')}
          </p>
        </div>

        <div className="card" style={{ maxWidth: 'none', marginTop: 14 }}>
          <h3 style={{ marginTop: 0 }}>{t('add.step3')}</h3>

          <label>{t('add.hasLatrine')}</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              [true, t('common.yes')],
              [false, t('common.no')],
              [null, t('common.unknown')],
            ].map(([v, l]) => (
              <button
                key={String(l)}
                onClick={() => setHasLatrine(v as boolean | null)}
                className={hasLatrine === v ? 'btn-primary' : ''}
                style={hasLatrine === v ? undefined : { border: '2px solid var(--line)', background: 'var(--white)' }}
              >
                {l as string}
              </button>
            ))}
          </div>

          {hasLatrine === true && (
            <>
              <label htmlFor="lt">{t('add.latrineType')}</label>
              <select id="lt" value={latrineType} onChange={(e) => setLatrineType(e.target.value)} style={selectStyle}>
                <option value="">{t('common.notRecorded')}</option>
                {opts('latrine_type').map((o) => (
                  <option key={o.code} value={o.code}>
                    {o.label_en}
                  </option>
                ))}
              </select>
            </>
          )}

          <label htmlFor="ws">{t('add.waterSource')}</label>
          <select id="ws" value={waterSource} onChange={(e) => setWaterSource(e.target.value)} style={selectStyle}>
            <option value="">{t('common.notRecorded')}</option>
            {opts('water_source').map((o) => (
              <option key={o.code} value={o.code}>
                {o.label_en}
              </option>
            ))}
          </select>
        </div>

        {candidates && candidates.length > 0 && (
          <div className={blocked ? 'alert alert-error' : 'alert alert-warn'} style={{ marginTop: 14 }}>
            <strong>
              {blocked
                ? t('add.dupBlocked')
                : t('add.dupWarn')}
            </strong>
            <ul style={{ margin: '10px 0 0', paddingLeft: 18 }}>
              {candidates.map((c) => (
                <li key={c.household_uid}>
                  {c.household_code ?? t('village.noNumber')} — {c.head_name ?? 'no name recorded'} ·{' '}
                  {Math.round(c.distance_m)} {t('add.metresAway')}
                  {c.name_similarity != null && c.name_similarity > 0.4 && (
                    <> · name {Math.round(c.name_similarity * 100)}% similar</>
                  )}
                </li>
              ))}
            </ul>

            {blocked ? (
              <p style={{ marginBottom: 0 }}>
                {t('add.dupBlockedHelp')}
              </p>
            ) : (
              <div style={{ marginTop: 12 }}>
                <button
                  onClick={() => {
                    setConfirmedNotDuplicate(true)
                    setCandidates(null)
                  }}
                >
                  {t('add.dupDifferent')}
                </button>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="alert alert-error" style={{ marginTop: 14 }}>
            {error}
          </div>
        )}

        <div style={{ margin: '18px 0 40px' }}>
          <button className="btn-primary" onClick={save} disabled={saving || capturing || !fix || blocked}>
            {saving ? t('common.saving') : t('add.submit')}
          </button>
          {!fix && <p className="muted">{t('add.captureFirst')}</p>}
        </div>
      </div>
    </div>
  )
}
